import React, {
  MouseEventHandler,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { Redirect } from 'react-router-dom';
import {
  FormContextProvider,
  SchemaFieldsWithContextUI,
} from '@stormwater-software/lib-form';
import { UIControlType } from '@stormwater-software/lib-form-control-types';
import { FormModal, SubmitHandlerFn } from '../../Shared/form';
import AppDivisionContext from '../../../contexts/AppDivisionContext';
import inspectionTemplateApi from '../../../utils/api/inspectionTemplate';
import inspectionTemplateTypesApi, {
  InspectionTemplateType,
} from '../../../utils/api/inspectionTemplateTypes';

interface Props {
  handleClose: MouseEventHandler;
  title: string;
  inspectionTemplate?: any;
  onUpdated?: () => void;
  enableRedirect?: boolean;
}

const getSchema = (inspectionTypes: InspectionTemplateType[]) => {
  return {
    name: {
      label: 'Name',
      validation: {
        required: true,
      },
    },
    inspection_type_id: {
      label: 'Inspection Type',
      controlType: UIControlType.select,
      options: inspectionTypes.map((type) => ({
        value: type.id,
        label: type.name,
      })),
      validation: {
        required: true,
      },
      placeholder: 'Select an inspection type',
    },
  };
};

const InspectionTemplateForm: React.FC<Props> = ({
  handleClose,
  title,
  inspectionTemplate,
  onUpdated,
  enableRedirect = true,
}) => {
  const { appDivisionId } = useContext(AppDivisionContext);

  const isEdit = Boolean(inspectionTemplate?.id);

  const [redirectId, setRedirectId] = useState(0);
  const [inspectionTypes, setInspectionTypes] = useState<
    InspectionTemplateType[]
  >([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        if (isEdit) {
          const { types, currentTypeId } =
            await inspectionTemplateTypesApi.getAvailableTypes(
              inspectionTemplate!.id!,
            );

          setInspectionTypes(types);

          inspectionTemplate!.inspection_type_id = currentTypeId;
        } else {
          const types = await inspectionTemplateTypesApi.index();

          setInspectionTypes(types);
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, []);

  const initialData = useMemo(() => {
    const data: any = {
      divisionId: appDivisionId,
      ...inspectionTemplate,
    };

    const exists = inspectionTypes.some(
      (t) => t.id === data.inspection_type_id,
    );

    if ((!exists || !data.inspection_type_id) && inspectionTypes.length) {
      data.inspection_type_id = inspectionTypes[0].id;
    }

    return data;
  }, [inspectionTemplate, inspectionTypes]);

  const handleSubmit: SubmitHandlerFn = async (formData) => {
    if (isEdit) {
      await inspectionTemplateApi.update(inspectionTemplate!.id!, formData);

      onUpdated?.();
      handleClose(undefined as any);
      
return;
    }

    const created = await inspectionTemplateApi.create(formData);

    if (enableRedirect) {
      setRedirectId(created.id);
    }

    handleClose(undefined as any);
  };

  if (redirectId) {
    return (
      <Redirect
        push
        to={`/divisions/${appDivisionId}/inspection-templates/${redirectId}`}
      />
    );
  }

  if (loading) {
    return (
      <div style={{ padding: 20, textAlign: 'center' }}>
        Loading inspection types...
      </div>
    );
  }

  return (
    <FormContextProvider>
      <FormModal
        onCancel={handleClose}
        onSubmit={handleSubmit}
        modalProps={{
          title,
        }}
      >
        <SchemaFieldsWithContextUI
          schema={getSchema(inspectionTypes)}
          initialFormData={initialData}
        />
      </FormModal>
    </FormContextProvider>
  );
};

export default InspectionTemplateForm;
